<template>
  <div class="export-btns-container">
    <div class="export-btns" v-if="data ? data.length : serverSide">
      <button
        v-if="hideExcel ? false : true"
        class="btn  export-excel hcm-btn hcm-btn-secondary hcm-btn-hover-primary"
        :class="{ 'is-exporting': loadingType }"
        @click="exportToExcel"
      >
        <i
          :class="
            loadingType === 'excel'
              ? 'fa-solid fa-spinner fa-spin'
              : 'fa-solid fa-file-excel'
          "
        ></i>  Export Excel
      </button>
      <button
        v-if="hidePdf ? false : true"
        class="btn  export-pdf hcm-btn hcm-btn-secondary hcm-btn-hover-primary"
        :class="{ 'is-exporting': loadingType }"
        @click="exportToPDF"
      >
        <i
          :class="
            loadingType === 'pdf'
              ? 'fa-solid fa-spinner fa-spin'
              : 'fa-solid fa-file-pdf'
          "
        ></i>  Export PDF
      </button>
    </div>

    <input
      v-if="hideSearch ? false : true"
      type="text"
      class="form-control"
      :value="inputValue"
      @input="handleChange"
      @focus="isFocused = true"
      @blur="isFocused = employee_id !== ''"
      placeholder="Search Table Data"
    />
  </div>
</template>

<script>
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

import jsPDF from "jspdf";
import "jspdf-autotable";
import { emits } from "@/staticData";
import { searchData, showApiError } from "@/utils";

export default {
  props: [
    "fileName",
    "data",
    "columns",
    "parent",
    "hidePdf",
    "hideExcel",
    "hideSearch",
    "payrollData",
    "secondaryColumns",
    "secondaryData",
    "tertiaryColumns",
    "tertiaryData",
    "initialSearch",
    // Server-side export: when true, the excel/pdf buttons call `serverExport`
    // (a parent-supplied fn `(type) => Promise<axiosResponse>`) instead of
    // building the file client-side from `data`/`columns`.
    "serverSide",
    "serverExport",
  ],
  emits: ["search-input-change"],
  data() {
    return {
      isFocusedOn: false,
      text: "",
      hasInteracted: false,
      loadingType: null, // "excel" | "pdf" | null
    };
  },
  watch: {
    initialSearch() {
      this.text = "";
      this.hasInteracted = false;
    },
  },
  computed: {
    inputValue() {
      return this.hasInteracted ? this.text : (this.initialSearch || "");
    },
  },
  methods: {
    async serverDownload(type) {
      if (this.loadingType || typeof this.serverExport !== "function") return;
      this.loadingType = type;
      try {
        const res = await this.serverExport(type);
        const blob = res?.data instanceof Blob ? res.data : res;
        const ext = type === "excel" ? "xlsx" : "pdf";
        saveAs(blob, `hcm-${this.fileName}.${ext}`);
      } catch (err) {
        showApiError(await this.normalizeBlobError(err));
      } finally {
        this.loadingType = null;
      }
    },
    // With responseType:"blob" an error body is also a Blob, so unwrap it to
    // JSON before handing it to showApiError.
    async normalizeBlobError(err) {
      const data = err?.response?.data;
      if (data instanceof Blob && data.type.includes("json")) {
        try {
          err.response.data = JSON.parse(await data.text());
        } catch (e) {
          /* keep original error */
        }
      }
      return err;
    },
    exportToExcel() {
      if (this.serverSide) return this.serverDownload("excel");
      // Create a new workbook and append the worksheet
      const workbook = XLSX.utils.book_new();

      // Create a worksheet from the items data
      const updatedData = [];
      this.data.map((el, index) => {
        updatedData.push({});
        Object.entries(el).forEach(([key, value]) => {
          this.columns.map((item) => {
            if (key === "id" || key === "key") {
              updatedData[index]["ID"] = index + 1;
            } else {
              if (key == item.dataIndex) {
                updatedData[index][item.title] = value;
              }
            }
          });
        });
      });
      const worksheet = XLSX.utils.json_to_sheet(updatedData);
      if (this.payrollData) {
        const baseNumericHeaders = [
          "Salary Per Month",
          "Basic Per Month",
          "Actual Salary As Per Attendance",
          "Arrears",
          "Advances",
          "Incentives",
          "Custom Additions",
          "Gross Salary",
          "Income Tax",
          "EOBI",
          "Penalty On NCNS",
          "Provident Fund",
          "Other Deductions",
          "Custom Deductions",
          "Total Deductions",
          "Net Salary",
        ];
        const baseCommentMapping = {
          Arrears: "arrears_reason",
          Advances: "advances_reason",
          Incentives: "incentives_reason",
          "Other Deductions": "other_deductions_reason",
          "Penalty On NCNS": "ncns_penality_reason",
          "Custom Additions": "custom_addition_reason",
          "Custom Deductions": "custom_deduction_reason",
        };

        // First, find the indices where "Custom Additions" and "Custom Deductions" appear
        const customAdditionsIndex = baseNumericHeaders.findIndex(
          (header) => header === "Custom Additions",
        );
        const customDeductionsIndex = baseNumericHeaders.findIndex(
          (header) => header === "Custom Deductions",
        );

        // Create arrays for breakdown headers and their mappings
        const additionBreakdownHeaders = [];
        const additionBreakdownMappings = {};
        const deductionBreakdownHeaders = [];
        const deductionBreakdownMappings = {};

        // Collect all custom addition breakdown columns
        Object.keys(this.data[0])?.forEach((key) => {
          if (key.startsWith("custom_addition_") && key !== "custom_addition") {
            let temp = this.columns.find((col) => col.dataIndex == key);
            if (temp) {
              additionBreakdownHeaders.push(temp?.title);
              additionBreakdownMappings[temp?.title] = `${key}_reason`;
            }
          }
          if (
            key.startsWith("custom_deduction_") &&
            key !== "custom_deduction"
          ) {
            let temp = this.columns.find((col) => col.dataIndex == key);
            if (temp) {
              deductionBreakdownHeaders.push(temp?.title);
              deductionBreakdownMappings[temp?.title] = `${key}_reason`;
            }
          }
        });

        // Create new baseNumericHeaders array with breakdown columns inserted at correct positions
        const numericHeaders = [...baseNumericHeaders];
        if (customAdditionsIndex !== -1) {
          // Remove "Custom Additions" from the array
          numericHeaders.splice(customAdditionsIndex, 1);

          // Insert addition breakdown headers at the same position
          numericHeaders.splice(
            customAdditionsIndex,
            0,
            ...additionBreakdownHeaders,
          );
          // Optionally, you can add "Custom Additions" back after the breakdowns if needed
          numericHeaders.splice(
            customAdditionsIndex + additionBreakdownHeaders.length,
            0,
            "Custom Additions",
          );
        }

        if (customDeductionsIndex !== -1) {
          // Note: The index might have changed if we added addition breakdowns before it
          const newCustomDeductionsIndex = numericHeaders.findIndex(
            (header) => header === "Custom Deductions",
          );

          if (newCustomDeductionsIndex !== -1) {
            // Remove "Custom Deductions" from the array
            numericHeaders.splice(newCustomDeductionsIndex, 1);

            // Insert deduction breakdown headers at the same position
            numericHeaders.splice(
              newCustomDeductionsIndex,
              0,
              ...deductionBreakdownHeaders,
            );

            // Optionally, you can add "Custom Deductions" back after the breakdowns if needed
            numericHeaders.splice(
              newCustomDeductionsIndex + deductionBreakdownHeaders.length,
              0,
              "Custom Deductions",
            );
          }
        }

        // Update baseCommentMapping with breakdown mappings
        const commentMapping = {
          ...baseCommentMapping,
          ...additionBreakdownMappings,
          ...deductionBreakdownMappings,
        };

        Object.keys(worksheet).forEach((cell) => {
          if (cell[0] === "!") return; // skip metadata

          const columnLetter = cell.replace(/[0-9]/g, "");
          const headerCell = worksheet[columnLetter + "1"];
          if (!headerCell) return;

          const headerTitle = headerCell.v;

          if (
            numericHeaders.includes(headerTitle) &&
            worksheet[cell].t === "n"
          ) {
            worksheet[cell].z = "#,##0";
          }

          const reasonKey = commentMapping[headerTitle];
          if (reasonKey) {
            this.data.forEach((row, rowIndex) => {
              const excelRow = rowIndex + 2;
              const cellRef = `${columnLetter}${excelRow}`;
              const reasonText = row[reasonKey];

              if (reasonText) {
                if (!worksheet[cellRef]) worksheet[cellRef] = { t: "s", v: "" };
                worksheet[cellRef].c = [
                  {
                    a: "System",
                    t: reasonText,
                  },
                ];
                worksheet[cellRef].c["hidden"] = true; // Keep comment hidden (show on hover)
              }
            });
          }
        });

        // Reorder columns to put numeric headers in correct positions while keeping all columns
        if (numericHeaders.length > 0) {
          // Get all existing headers from the worksheet
          const allHeaders = [];
          const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1:A1");

          for (let col = range.s.c; col <= range.e.c; col++) {
            const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
            const headerCell = worksheet[cellAddress];
            if (headerCell) {
              allHeaders.push(headerCell.v);
            }
          }

          // Create the final header order: all non-numeric headers + numeric headers in correct positions
          const finalHeaderOrder = [];

          // First, add all headers that are NOT in numericHeaders
          allHeaders.forEach((header) => {
            if (!numericHeaders.includes(header)) {
              finalHeaderOrder.push(header);
            }
          });

          // Find where to insert numericHeaders (maintaining their relative positions among themselves)
          // Since numericHeaders already has the correct order, we need to find the right insertion point
          if (finalHeaderOrder.length > 0) {
            // Look for the first numeric header that appears in the original order to determine insertion point
            let insertionIndex = finalHeaderOrder.length;
            for (let i = 0; i < allHeaders.length; i++) {
              if (numericHeaders.includes(allHeaders[i])) {
                // Find where this numeric header should be in finalHeaderOrder
                const originalPosition = allHeaders.indexOf(allHeaders[i]);
                // Find how many non-numeric headers came before it
                let nonNumericCount = 0;
                for (let j = 0; j < originalPosition; j++) {
                  if (!numericHeaders.includes(allHeaders[j])) {
                    nonNumericCount++;
                  }
                }
                insertionIndex = nonNumericCount;
                break;
              }
            }

            // Insert numericHeaders at the calculated position
            finalHeaderOrder.splice(insertionIndex, 0, ...numericHeaders);
          } else {
            finalHeaderOrder.push(...numericHeaders);
          }

          // Create new worksheet with the correct column order
          const newWorksheet = XLSX.utils.json_to_sheet([], {
            header: finalHeaderOrder,
          });

          // Copy all data from original worksheet to new one with correct order
          for (let row = 1; row <= range.e.r + 1; row++) {
            const rowData = {};
            finalHeaderOrder.forEach((header) => {
              // Find the column where this header exists in original worksheet
              let originalCol = null;
              for (let col = range.s.c; col <= range.e.c; col++) {
                const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
                const headerCell = worksheet[cellAddress];
                if (headerCell && headerCell.v === header) {
                  originalCol = col;
                  break;
                }
              }

              if (originalCol !== null) {
                const cellAddress = XLSX.utils.encode_cell({
                  r: row,
                  c: originalCol,
                });
                const value = worksheet[cellAddress]?.v;
                if (value !== undefined) {
                  rowData[header] = value;
                }
              }
            });

            if (Object.keys(rowData).length > 0) {
              XLSX.utils.sheet_add_json(newWorksheet, [rowData], {
                skipHeader: true,
                origin: -1,
                header: finalHeaderOrder,
              });
            }
          }

          // Replace worksheet with reordered one
          Object.keys(worksheet).forEach((key) => delete worksheet[key]);
          Object.assign(worksheet, newWorksheet);
        }

        XLSX.utils.book_append_sheet(workbook, worksheet, "Payroll");

        if (
          this.secondaryData?.length > 0 &&
          this.secondaryColumns?.length > 0
        ) {
          const updatedSecData = [];
          this.secondaryData.map((el, index) => {
            updatedSecData.push({});
            Object.entries(el).forEach(([key, value]) => {
              this.secondaryColumns.map((item) => {
                if (key === "id" || key === "key") {
                  updatedSecData[index]["ID"] = index + 1;
                } else {
                  if (key == item.dataIndex) {
                    updatedSecData[index][item.title] = value;
                  }
                }
              });
            });
          });
          const secWorksheet = XLSX.utils.json_to_sheet(updatedSecData);
          XLSX.utils.book_append_sheet(workbook, secWorksheet, "Attendance");
        }

        if (this.tertiaryData?.length > 0 && this.tertiaryColumns?.length > 0) {
          const updatedTerData = [];
          this.tertiaryData.map((el, index) => {
            updatedTerData.push({});
            Object.entries(el).forEach(([key, value]) => {
              this.tertiaryColumns.map((item) => {
                if (key === "id" || key === "key") {
                  updatedTerData[index]["ID"] = index + 1;
                } else {
                  if (key == item.dataIndex) {
                    updatedTerData[index][item.title] = value;
                  }
                }
              });
            });
          });
          const terWorksheet = XLSX.utils.json_to_sheet(updatedTerData);
          XLSX.utils.book_append_sheet(workbook, terWorksheet, "Leaves");
        }
      } else {
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      }

      // Convert the workbook to a binary string
      const workbookOut = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "binary",
        cellStyles: true,
      });

      // Convert the binary string to a Blob and save it
      const blob = new Blob([this.s2ab(workbookOut)], {
        type: "application/octet-stream",
      });

      saveAs(blob, `hcm-${this.fileName}.xlsx`);
    },
    s2ab(s) {
      const buf = new ArrayBuffer(s.length);
      const view = new Uint8Array(buf);
      for (let i = 0; i < s.length; i++) {
        view[i] = s.charCodeAt(i) & 0xff;
      }
      return buf;
    },

    exportToPDF() {
      if (this.serverSide) return this.serverDownload("pdf");
      const doc = new jsPDF({
        orientation: "landscape",
      });
      const headers = [];
      this.columns.forEach((el) => {
        if (el.key !== "action") {
          headers.push(el.title);
        }
      });

      console.log("exportToPDF", this.columns, headers);

      // const headers = this.columns.filter().map((field) => field.title);
      const data = this.data.map((item, index) =>
        this.columns.map((field) => {
          if (field.dataIndex == "id" || field.dataIndex == "key") {
            return index + 1;
          } else {
            return item[field.dataIndex];
          }
        }),
      );

      doc.autoTable({
        head: [headers],
        body: data,
        columnStyles: {
          0: { cellWidth: "auto" }, // Set the width for the 'name' column
        },
      });

      doc.save(`hcm-${this.fileName}.pdf`);
    },

    handleChange(ev) {
      const value = ev.target.value;
      this.text = value;
      this.hasInteracted = true;
      if (this.parent?.filteredData && this.parent?.data) {
        this.parent.filteredData = searchData(value, this.parent.data);
      }
    },
    // WIP Dynamic filtered Data list
    // handleChange(ev) {
    //   const value = ev.target.value;

    //   // Find the dynamic key in the parent that matches the passed data
    //   const parentKey = Object.keys(this.parent).find(
    //     (key) => this.parent[key] === this.data
    //   );

    //   // const data = this.data;
    //   console.log("p key", parentKey);
    //   console.log("p obj", this.parent);
    //   console.log("p data", this.data);
    //   // console.log('p data local', data)

    //   if (parentKey) {
    //     const data = this.parent.data;
    //     if (value.trim() === "") {
    //       // Reset to the original data when the input is cleared
    //       console.log("p obj data", data);
    //       this.parent[parentKey] = [...data]; // Ensure `data` in the parent holds the original list
    //     } else {
    //       // Apply filtering
    //       this.parent[parentKey] = searchData(value, this.data);
    //     }
    //   } else {
    //     console.warn(
    //       "The parent does not have a property matching the passed 'data' prop."
    //     );
    //   }
    // },
  },
};
</script>


<style scoped>
/* Keep the button's brand background while an export is running; only dim it.
   (Avoids Bootstrap's `.btn.disabled` rules, which reset the background.) */
.export-btns .btn.is-exporting {
  pointer-events: none;
  opacity: 0.6;
}

/* Export button styling with subtle inner box shadow */
.export-btn {
  background: #f0643b;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 10px 18px;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;
  box-shadow: inset 0 0 4px rgba(0,0,0,0.1);
  gap: 10px;
}

.export-btn:hover {
  background: #d84f27;
  box-shadow: inset 0 0 6px rgba(0,0,0,0.15);
}
</style>
